import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Grid,
  Button,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

//constants
import { userAgencyOptions } from "../../Constants";

//themes
import themes from "../../themes/defaultTheme";

//Update regions list
const modifyRegionsLst = (userRegionsLst) => {
  if (userRegionsLst.length > 1) {
    // Remove "All" from the list if it exists.
    const indexOfAll = userRegionsLst.indexOf("All");
    if (indexOfAll > -1) userRegionsLst.splice(indexOfAll, 1);

    // Sort the regions alphabetically
    const sortedRegions = userRegionsLst.sort((a, b) => a.localeCompare(b));

    // Add "All" back to the top of the list
    return ["All", ...sortedRegions];
  }
  return userRegionsLst;
};

export default function NonSystemUserEditDialog({
  open,
  onClose,
  onConfirm,
  email: initialEmail,
  name: initialName,
  region: initialRegion,
  role: initialRole,
  agency: initialAgency,
  active: initialActive,
  email1: initialEmail1,
  email2: initialEmail2,
  email3: initialEmail3,
  loggedInUser
}) {
  const userRegionsLst = loggedInUser?.regions
    ? loggedInUser.regions.split(",")
    : [];
  const userRegionOptions = modifyRegionsLst(userRegionsLst);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [region, setRegion] = useState(null);
  const [agency, setAgency] = useState(null);
  const [active, setActive] = useState(null);
  const [email1, setEmail1] = useState(null);
  const [email2, setEmail2] = useState(null);
  const [email3, setEmail3] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setEmail(initialEmail || "");
    setName(initialName || "");
    setRole(initialRole || "");
    setRegion(initialRegion || null);
    setAgency(initialAgency || null);
    setActive(initialActive || null);
    setEmail1(initialEmail1 || null);
    setEmail2(initialEmail2 || null);
    setEmail3(initialEmail3 || null);
  }, [initialEmail, initialName, initialRole, initialRegion, initialAgency, initialActive, initialEmail1, initialEmail2, initialEmail3]);

  const handleCancel = () => {
    setErrors({});
    onClose();
  };

  const handleConfirm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Name is required.';
    if (!region) newErrors.region = 'Region is required.';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    onConfirm(email, name, role, region, agency, active, email1, email2, email3);
    setEmail("");
    setName("");
    setRole("");
    setRegion(null);
    setAgency(null);
    setActive(null);
    setEmail1(null);
    setEmail2(null);
    setEmail3(null);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }} aria-labelledby="dialog-title" sx={{ zIndex: 2000 }}>
      <DialogTitle
        id="dialog-title"
        style={{ textAlign: "center", fontWeight: "bold" }}
      >
        Edit Details
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Info */}
          <Grid item xs={12}>
            <Typography variant="caption" style={{ fontStyle: "italic" }}>
              Fields marked with <em style={{ color: themes.default.palette.error.dark }}>*</em> are required.
            </Typography>
          </Grid>

          {/* Email label and Textfield */}
          <Grid item xs={6}>
            <InputLabel htmlFor="email-text" id="email-label">
              Email <em style={{ color: themes.default.palette.error.dark }}>*</em>
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="email"
              fullWidth
              variant="outlined"
              value={email}
              disabled={true}
              inputProps={{
                id: 'email-text',
                'aria-labelledby': 'email-label'
              }}
            />
          </Grid>

          {/* Name label and Textfield */}
          <Grid item xs={6}>
            <InputLabel htmlFor="name-text" id="name-label">
              Name <em style={{ color: themes.default.palette.error.dark }}>*</em>
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="name"
              fullWidth
              variant="outlined"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }}
              error={!!errors.name}
              helperText={errors.name || ''}
              inputProps={{
                id: 'name-text',
                'aria-labelledby': 'name-label'
              }}
            />
          </Grid>

          {/* Region label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="region-autocomplete" id="region-select-label">
              Region <em style={{ color: themes.default.palette.error.dark }}>*</em>
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="region-autocomplete"
              options={userRegionOptions || []}
              value={region || null}
              onChange={(event, newValue) => setRegion(newValue || "")}
              disabled={userRegionOptions?.length === 1}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth
                  error={!!errors.region}
                  helperText={errors.region || ''}
                />
              )}
              slotProps={{
                popper: {
                  sx: { zIndex: 2001 }
                }
              }}
            />
          </Grid>

          {/* Role label and textfield */}
          <Grid item xs={6}>
            <InputLabel htmlFor="role-text" id="role-label">Role</InputLabel>
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="role"
              fullWidth
              variant="outlined"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              inputProps={{
                id: 'role-text',
                'aria-labelledby': 'role-label'
              }}
            />
          </Grid>

          {/* Agency label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="agency-autocomplete" id="agency-select-label">
              Agency
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="agency-autocomplete"
              options={userAgencyOptions.map(option => option.label) || []}
              value={agency || null}
              onChange={(event, newValue) => setAgency(newValue || "")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
              slotProps={{
                popper: {
                  sx: { zIndex: 2001 }
                }
              }}
            />
          </Grid>

          {/* Active label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="active-autocomplete" id="active-select-label">
              Active
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="active-autocomplete"
              options={[{ label: "Yes", value: "Yes" }, { label: "No", value: "No" }].map(option => option.label) || []}
              value={active || null}
              onChange={(event, newValue) => setActive(newValue || "")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
              slotProps={{
                popper: {
                  sx: { zIndex: 2001 }
                }
              }}
            />
          </Grid>

          {/* Reminder 1 label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="email1-autocomplete" id="email1-select-label">
              Reminder 1
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="email1-autocomplete"
              options={[{ label: "to", value: "to" }, { label: "cc", value: "cc" }].map(option => option.label) || []}
              value={email1 || null}
              onChange={(event, newValue) => setEmail1(newValue || "")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
              slotProps={{
                popper: {
                  sx: { zIndex: 2001 }
                }
              }}
            />
          </Grid>

          {/* Reminder 2 label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="email2-autocomplete" id="email2-select-label">
              Reminder 2
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="email2-autocomplete"
              options={[{ label: "to" }, { label: "cc" }].map(option => option.label) || []}
              value={email2 || null}
              onChange={(event, newValue) => setEmail2(newValue || "")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
              slotProps={{
                popper: {
                  sx: { zIndex: 2001 }
                }
              }}
            />
          </Grid>

          {/* Reminder3 3 label and Autocomplete */}
          <Grid item xs={6}>
            <InputLabel htmlFor="email3-autocomplete" id="email3-select-label">
              Reminder 3
            </InputLabel>
          </Grid>
          <Grid item xs={6}>
            <Autocomplete
              id="email3-autocomplete"
              options={[{ label: "to" }, { label: "cc" }].map(option => option.label) || []}
              value={email3 || null}
              onChange={(event, newValue) => setEmail3(newValue || "")}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" fullWidth />
              )}
              slotProps={{
                popper: {
                  sx: { zIndex: 2001 }
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>
          Cancel
        </Button>
        <Button type="submit" onClick={handleConfirm} color="primary">
          Edit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
